import { useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from '@/hooks/queries/useUsers'
import { UsersToolbar } from '@/pages/users/components/UsersToolbar'
import type { SystemUser, UserFormData, UserRole } from '@/types'

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; user: SystemUser }
  | { mode: 'delete'; user: SystemUser }

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrador',
  operator: 'Operador',
  client: 'Cliente',
}

const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'bg-primary/25 text-secondary',
  operator: 'bg-[#b9d94a]/30 text-[#383f2d]',
  client: 'bg-[#7c8c43]/20 text-[#383f2d]',
}

const EMPTY_FORM: UserFormData = { name: '', email: '', role: 'operator', password: '' }

function getServerError(error: unknown): string | null {
  if (!error) return null
  const err = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
  const errors = err.response?.data?.errors
  if (errors) return Object.values(errors).flat().join(' ')
  return err.response?.data?.message ?? 'Ocurrió un error inesperado'
}

export function UsersPage() {
  const { data: users, isLoading } = useUsers()
  const createMutation = useCreateUser()
  const updateMutation = useUpdateUser()
  const deleteMutation = useDeleteUser()

  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' })
  const [form, setForm] = useState<UserFormData>(EMPTY_FORM)
  const [search, setSearch] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([])

  const openCreate = () => {
    setForm(EMPTY_FORM)
    createMutation.reset()
    setDialog({ mode: 'create' })
  }

  const openEdit = (user: SystemUser) => {
    setForm({ name: user.name, email: user.email, role: user.role, password: '' })
    updateMutation.reset()
    setDialog({ mode: 'edit', user })
  }

  const openDelete = (user: SystemUser) => {
    deleteMutation.reset()
    setDialog({ mode: 'delete', user })
  }

  const closeDialog = () => setDialog({ mode: 'closed' })

  const handleSubmit = async () => {
    if (dialog.mode === 'create') {
      await createMutation.mutateAsync(form)
      closeDialog()
    } else if (dialog.mode === 'edit') {
      await updateMutation.mutateAsync({ id: dialog.user.id, data: form })
      closeDialog()
    }
  }

  const handleDelete = async () => {
    if (dialog.mode !== 'delete') return
    await deleteMutation.mutateAsync(dialog.user.id)
    closeDialog()
  }

  const clearFilters = () => {
    setSearch('')
    setSelectedRoles([])
  }

  const hasActiveFilters = Boolean(search.trim() || selectedRoles.length)
  const normalizedSearch = search.trim().toLowerCase()
  const filteredUsers = (users ?? []).filter((user) => {
    const matchesRole = !selectedRoles.length || selectedRoles.includes(user.role)

    if (!matchesRole) return false
    if (!normalizedSearch) return true

    const searchableText = [
      user.name,
      user.email,
      user.role,
      ROLE_LABELS[user.role],
    ]
      .join(' ')
      .toLowerCase()

    return searchableText.includes(normalizedSearch)
  })

  const isFormDialog = dialog.mode === 'create' || dialog.mode === 'edit'
  const activeMutation = dialog.mode === 'create' ? createMutation : updateMutation
  const formError = isFormDialog ? getServerError(activeMutation.error) : null
  const deleteError = dialog.mode === 'delete' ? getServerError(deleteMutation.error) : null

  const isFormValid = form.name.trim() && form.email.trim() && form.role &&
    (dialog.mode === 'edit' || (form.password && form.password.length >= 8))

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Usuarios del sistema</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <UsersToolbar
            search={search}
            onSearchChange={setSearch}
            selectedRoles={selectedRoles}
            onSelectedRolesChange={setSelectedRoles}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : !(users?.length) ? (
            <p className="text-sm text-muted-foreground">No hay usuarios registrados.</p>
          ) : !filteredUsers.length ? (
            <p className="text-sm text-muted-foreground">
              No se encontraron usuarios con los filtros seleccionados.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                  <th className="pb-2">Nombre</th>
                  <th className="pb-2">Email</th>
                  <th className="pb-2">Rol</th>
                  <th className="pb-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-3 font-medium text-foreground">{u.name}</td>
                    <td className="py-3 text-foreground/70">{u.email}</td>
                    <td className="py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[u.role]}`}>
                        {ROLE_LABELS[u.role]}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mr-1 h-8 w-8"
                        onClick={() => openEdit(u)}
                        aria-label={`Editar ${u.name}`}
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openDelete(u)}
                        aria-label={`Desactivar ${u.name}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={isFormDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'create' ? 'Nuevo usuario' : 'Editar usuario'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="user-name">Nombre</Label>
              <Input
                id="user-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-role">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v as UserRole }))}>
                <SelectTrigger id="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="operator">Operador</SelectItem>
                  <SelectItem value="client">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dialog.mode === 'create' && (
              <div className="space-y-1">
                <Label htmlFor="user-password">Contraseña</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={form.password ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
            )}
            {formError && <p className="text-sm text-red-600">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || activeMutation.isPending}
            >
              {activeMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialog.mode === 'delete'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desactivar usuario</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70">
            ¿Estás seguro que deseas desactivar a{' '}
            <span className="font-semibold">
              {dialog.mode === 'delete' ? dialog.user.name : ''}
            </span>
            ? El usuario no podrá iniciar sesión.
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Desactivando...' : 'Desactivar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
